/**
 * Marketing Terminal — Engagement Inbox
 *
 * Bidirectional inbox: every comment/reply/mention/DM on our owned platform
 * accounts. Cowork drafts a response; admin edits + approves; portal posts
 * via channel API (Phase 3b adds the platform-side reply call).
 *
 * Master spec §2.3 + audit gap F2 (SLA-aware triage tiers).
 *
 * Backend:
 *   GET  /api/admin/marketing/engagement
 *   GET  /api/admin/marketing/engagement/:id
 *   PUT  /api/admin/marketing/engagement/:id/draft
 *   POST /api/admin/marketing/engagement/:id/send
 *   POST /api/admin/marketing/engagement/:id/dismiss
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Check,
  Clock,
  ExternalLink,
  Inbox,
  MessageSquare,
  Send,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface EngagementItem {
  id: string;
  platform: string;
  item_type: string;
  platform_item_id: string;
  parent_post_id: string | null;
  parent_post_text?: string | null;
  thread_context: Array<{ author?: string; text?: string; posted_at?: string }>;
  author_handle: string | null;
  author_display_name: string | null;
  author_avatar_url: string | null;
  author_external_url: string | null;
  content_text: string | null;
  content_url: string | null;
  received_at: string;
  sentiment_score: number | null;
  suggested_action: string | null;
  triage_tier: string | null;
  status: string;
  drafted_response: string | null;
  drafted_by_agent: boolean;
  sent_response: string | null;
  sent_at: string | null;
  sla_window_seconds: number | null;
  sla_breach_at: string | null;
  sla_status: string | null;
}

interface InboxResponse {
  success: boolean;
  data: EngagementItem[];
  counts_by_status: Record<string, number>;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000 } } });

export function EngagementInbox() {
  return (
    <QueryClientProvider client={queryClient}>
      <InboxInner />
    </QueryClientProvider>
  );
}

function InboxInner() {
  const [statusFilter, setStatusFilter] = useState('new');
  const [platformFilter, setPlatformFilter] = useState('');
  const [selected, setSelected] = useState<EngagementItem | null>(null);

  const { data, isLoading } = useQuery<InboxResponse>({
    queryKey: ['marketing-engagement', statusFilter, platformFilter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (platformFilter) qs.set('platform', platformFilter);
      return adminFetch<InboxResponse>(`/api/admin/marketing/engagement?${qs.toString()}`);
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1
          className="text-2xl font-bold"
          style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}
        >
          Engagement Inbox
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Comments, replies, mentions, and DMs on your owned accounts.
          Cowork drafts; you edit + approve.
        </p>
      </div>

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-3"
        style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
      >
        <div className="flex gap-1">
          {['new', 'drafted', 'sent', 'dismissed', ''].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className="rounded px-2 py-1 text-xs font-medium"
              style={
                statusFilter === s
                  ? { backgroundColor: 'var(--cedar-primary)', color: '#fff' }
                  : { backgroundColor: 'var(--cedar-limestone)', color: 'var(--text-secondary)' }
              }
            >
              {s || 'all'}
              {s && data?.counts_by_status?.[s] !== undefined && (
                <span className="ml-1 text-[10px] opacity-75">{data.counts_by_status[s]}</span>
              )}
            </button>
          ))}
        </div>
        <select
          value={platformFilter}
          onChange={e => setPlatformFilter(e.target.value)}
          className="rounded border border-gray-200 px-2 py-1 text-xs"
        >
          <option value="">All platforms</option>
          {['linkedin', 'x', 'facebook', 'instagram', 'tiktok', 'reddit', 'youtube'].map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {/* List */}
        <div className="lg:col-span-5 space-y-2">
          {isLoading && <div className="text-sm text-gray-500">Loading inbox...</div>}
          {!isLoading && (data?.data?.length ?? 0) === 0 && (
            <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
              <Inbox className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              Inbox is empty for this filter.
            </div>
          )}
          {data?.data?.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              isSelected={selected?.id === item.id}
              onClick={() => setSelected(item)}
            />
          ))}
        </div>

        {/* Detail pane */}
        <div className="lg:col-span-7">
          {selected ? (
            <ItemDetail item={selected} onClose={() => setSelected(null)} />
          ) : (
            <div
              className="rounded-lg bg-white p-12 text-center text-sm text-gray-400"
              style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}
            >
              <MessageSquare className="mx-auto mb-2 h-10 w-10 text-gray-300" />
              Select a conversation to view + respond
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  isSelected,
  onClick,
}: {
  item: EngagementItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  const slaColor =
    item.sla_status === 'breached' ? 'var(--status-error)' :
    item.sla_status === 'at_risk' ? 'var(--status-warning)' :
    null;

  return (
    <button
      onClick={onClick}
      className={`block w-full rounded-lg border-2 bg-white p-3 text-left transition-colors hover:border-gray-200 ${
        isSelected ? 'border-green-600' : 'border-transparent'
      }`}
      style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}
    >
      <div className="mb-1 flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">
            {item.platform}
          </span>
          <span className="text-gray-500">{item.item_type}</span>
          {item.drafted_by_agent && (
            <span style={{ color: 'var(--cedar-primary)' }} title="Drafted by Cowork">
              <Sparkles className="h-3 w-3" />
            </span>
          )}
        </div>
        {slaColor && (
          <span
            className="font-medium uppercase"
            style={{ color: slaColor }}
            title={`SLA breach at ${item.sla_breach_at}`}
          >
            <AlertTriangle className="mr-0.5 inline h-2.5 w-2.5" />
            {item.sla_status}
          </span>
        )}
      </div>

      <div className="text-xs font-semibold text-gray-900">
        {item.author_display_name || item.author_handle || 'Unknown'}
      </div>

      <div className="mt-1 line-clamp-2 text-xs text-gray-700">
        {item.content_text || <em className="text-gray-400">(no text)</em>}
      </div>

      <div className="mt-1 flex items-center justify-between text-[10px] text-gray-400">
        <span>{new Date(item.received_at).toLocaleString()}</span>
        {item.status !== 'new' && (
          <span
            className="rounded px-1.5 py-0.5 font-medium"
            style={{
              backgroundColor: item.status === 'sent' ? 'var(--cedar-subtle)' : 'var(--cedar-limestone)',
              color: item.status === 'sent' ? 'var(--cedar-primary)' : 'var(--text-secondary)',
            }}
          >
            {item.status}
          </span>
        )}
      </div>
    </button>
  );
}

function ItemDetail({ item, onClose }: { item: EngagementItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(item.drafted_response || '');

  const saveDraft = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/engagement/${item.id}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ drafted_response: draft }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-engagement'] }),
  });

  const send = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/engagement/${item.id}/send`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-engagement'] }),
  });

  const dismiss = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/marketing/engagement/${item.id}/dismiss`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-engagement'] });
      onClose();
    },
  });

  // Re-sync draft state when switching items
  React.useEffect(() => {
    setDraft(item.drafted_response || '');
  }, [item.id, item.drafted_response]);

  const slaText = item.sla_breach_at
    ? `Reply by ${new Date(item.sla_breach_at).toLocaleTimeString()} for max algorithmic boost`
    : null;

  return (
    <div className="space-y-3 rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {item.author_avatar_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={item.author_avatar_url} alt="" className="h-10 w-10 rounded-full" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <User className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <div>
            <div className="text-sm font-bold text-gray-900">
              {item.author_display_name || item.author_handle || 'Unknown author'}
            </div>
            <div className="text-xs text-gray-500">
              {item.platform} · {item.item_type} · {new Date(item.received_at).toLocaleString()}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      {slaText && (
        <div
          className="flex items-center gap-1 rounded px-2 py-1 text-xs"
          style={{
            backgroundColor: item.sla_status === 'breached' ? 'var(--status-error-bg-soft)' : item.sla_status === 'at_risk' ? 'var(--note-bg)' : 'var(--cedar-subtle)',
            color: item.sla_status === 'breached' ? 'var(--status-error)' : item.sla_status === 'at_risk' ? 'var(--status-warning)' : 'var(--cedar-primary)',
          }}
        >
          <Clock className="h-3 w-3" />
          {slaText}
        </div>
      )}

      {item.parent_post_text && (
        <div className="rounded border-l-4 border-gray-200 bg-gray-50 p-2 text-xs">
          <div className="mb-1 font-medium text-gray-500">In response to your post:</div>
          <div className="text-gray-700 line-clamp-3">{item.parent_post_text}</div>
        </div>
      )}

      {item.thread_context?.length > 0 && (
        <div className="space-y-1 rounded bg-gray-50 p-2 text-xs">
          <div className="font-medium text-gray-500">Thread context:</div>
          {item.thread_context.map((c, i) => (
            <div key={i} className="text-gray-700">
              <strong>{c.author}:</strong> {c.text}
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Their message</div>
        <div className="rounded bg-gray-50 p-3 text-sm text-gray-800">
          {item.content_text || <em className="text-gray-400">(no text)</em>}
        </div>
        {item.content_url && (
          <a
            href={item.content_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <ExternalLink className="h-3 w-3" /> Open on platform
          </a>
        )}
      </div>

      {item.status === 'sent' ? (
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--cedar-primary)' }}>
            Your reply (sent {item.sent_at && new Date(item.sent_at).toLocaleString()})
          </div>
          <div className="rounded bg-green-50 p-3 text-sm text-gray-800" style={{ borderLeft: '4px solid var(--cedar-primary)' }}>
            {item.sent_response}
          </div>
        </div>
      ) : (
        <>
          <div>
            <div className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <span>Your reply</span>
              {item.drafted_by_agent && (
                <span style={{ color: 'var(--cedar-primary)' }}>
                  <Sparkles className="mr-0.5 inline h-3 w-3" />
                  Drafted by Cowork — edit before sending
                </span>
              )}
            </div>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={6}
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm font-mono"
              placeholder="Write your reply..."
            />
            <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
              <span>{draft.length} chars</span>
              {item.platform === 'x' && draft.length > 280 && (
                <span className="text-red-600">Over X 280-char limit — will split into thread</span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => dismiss.mutate()}
              disabled={dismiss.isPending}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => saveDraft.mutate()}
                disabled={!draft || saveDraft.isPending}
                className="rounded border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
              >
                {saveDraft.isPending ? 'Saving...' : 'Save draft'}
              </button>
              <button
                onClick={() => {
                  if (saveDraft.data || draft === item.drafted_response) {
                    send.mutate();
                  } else {
                    saveDraft.mutate(undefined, { onSuccess: () => send.mutate() });
                  }
                }}
                disabled={!draft || send.isPending}
                className="rounded px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--cedar-primary)' }}
              >
                <Send className="mr-1 inline h-3 w-3" />
                {send.isPending ? '...' : 'Approve & Send'}
              </button>
            </div>
          </div>
          {send.isSuccess && (
            <div className="rounded bg-green-50 p-2 text-xs text-green-800">
              <Check className="mr-1 inline h-3 w-3" />
              Marked sent. (Phase 3a stub: not yet posted to platform — copy + paste manually for now. Phase 3b adds platform-side reply.)
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EngagementInbox;
